import Box from '@eversports/klimt-primitives/legacy/Box'
import Link from '@eversports/klimt-primitives/legacy/Link'
import Stack from '@eversports/klimt-primitives/legacy/Stack'
import Text from '@eversports/klimt-primitives/legacy/Text'

import { VenueProfileTabsPrefix } from '../../../../App.types'
import { Localized, useIntl } from '../../../../localization/react'
import type { EventBookableItem, EventSession } from '../../ActivityDetails.types'
import ContentWrapper from '../../components/ContentWrapper'
import WrappedHeader from '../../components/WrappedHeader'
import FriendsAttending from '../../FriendAttending'
import checkHasOneSession from '../../helpers/check-has-one-session'
import DateTimeAvailability from '../../Sidebar/DateTimeAvailability'
import { SpotBooking } from '../../Sidebar/SpotBooking'
import { EventTypeToNameMapping } from '../Overview.constants'

import CalendarButton from './CalendarButton'
import Description from './Description'
import Difficulty from './Difficulty'
import FocusImageWithBackdrop from './FocusImageWithBackdrop'
import Location from './Location'
import Room from './Room'
import SessionComment from './SessionComment'
import ShareButton from './ShareButton'

interface Props {
  activity: EventBookableItem
  eventSessions: Array<EventSession>
}

const InfoSection = ({ activity, eventSessions }: Props) => {
  const intl = useIntl()

  const {
    activityName,
    description,
    eventType,
    activityImage,
    venue,
    level,
    sport,
    availableSpots,
    registrationState,
    locations,
    sessionComment,
    floorPlan,
  } = activity

  const hasOneSession = checkHasOneSession(eventSessions)
  const hasSpotBooking = Boolean(floorPlan?.id)

  return (
    <Stack>
      <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
        <FocusImageWithBackdrop activityImage={activityImage} activityName={activityName} venueName={venue.name} />
      </Box>
      <ContentWrapper>
        <Stack gap={2}>
          <Stack direction="row" justifyContent="space-between" gap={{ xs: 6, md: 8 }}>
            <WrappedHeader headerText={activityName} />
            <Stack direction="row" gap={3}>
              <ShareButton eventId={activity.id} />
              {hasOneSession && (
                <CalendarButton
                  activityName={activityName}
                  eventType={eventType}
                  sportName={sport.name}
                  eventSession={eventSessions[0]}
                />
              )}
            </Stack>
          </Stack>
          <Stack direction={{ sm: 'row' }} gap={1} flexWrap="wrap">
            <Stack direction="row" gap={1}>
              <Text color="darkgray">{sport.name}</Text>
              <Text color="darkgray" sx={{ textTransform: 'lowercase' }}>
                <Localized id={EventTypeToNameMapping[eventType]} />
              </Text>
            </Stack>
            <Link
              to={`${VenueProfileTabsPrefix.OVERVIEW}${venue.slug}`}
              blank
              ariaLabel={intl.activityDetailsVenueProfileLinkAriaLabel({ venueName: venue.name })}
            >
              <Text color="midnight" sx={{ textDecoration: 'underline' }}>{`@ ${venue.name}`}</Text>
            </Link>
          </Stack>
        </Stack>
        {level && <Difficulty difficulty={level} />}
        <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
          <FocusImageWithBackdrop activityImage={activityImage} activityName={activityName} venueName={venue.name} />
        </Box>
        <Stack gap={6}>
          <Box sx={{ display: { xs: 'block', md: 'none' } }}>
            <FriendsAttending activityId={activity.id} />
          </Box>
          {description && <Description description={description.html} />}
          {sessionComment && <SessionComment sessionComment={sessionComment} />}
          <Box sx={{ display: { md: 'none' } }}>
            <DateTimeAvailability
              eventSessions={eventSessions}
              availableSpots={availableSpots}
              registrationState={registrationState}
            />
          </Box>
          <Box sx={{ display: { md: 'none' } }}>
            {hasSpotBooking && <SpotBooking goToPreviewSpot={`/activity/${activity.id}/spots`} />}
          </Box>
        </Stack>
        <Room activity={activity} />
        <Location locations={locations} eventSessions={eventSessions} />
      </ContentWrapper>
    </Stack>
  )
}

export default InfoSection
